import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchChapterById, updateChapter } from "../../../apis/CourseApis";
import { useAuth } from "../../Authentication/AuthContext";
import { useToastHelpers } from "../../Toasts/ToastUtils";
import { useTheme } from "../../Theme/ThemeContext";
import { SkeletonFormPage } from "../../Skeletons";
import SeoMeta from "../../SeoMeta";

export default function EditChapter() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const [courseId, setCourseId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Load chapter data
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    
    const loadChapter = async () => {
      try {
        const chapter = await fetchChapterById(chapterId);
        if (isMounted) {
          setFormData({
            title: chapter.title || "",
            description: chapter.description || "",
          });
          setCourseId(chapter.courseId);
        }
      } catch (err) {
        console.error("Error loading chapter:", err);
        if (isMounted) {
          errorToast("Failed to load chapter");
          navigate("/courses");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadChapter();
    return () => { isMounted = false; };
  }, [chapterId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Chapter title is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Chapter description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const chapterData = {
        ...formData,
        courseId: courseId,
        updatedAt: Date.now(),
      };

      await updateChapter(chapterId, chapterData, user?.token);
      successToast("Chapter updated successfully!");
      navigate(`/chapter/${chapterId}`);
    } catch (err) {
      console.error("Error updating chapter:", err);
      errorToast("Failed to update chapter. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <SkeletonFormPage />;
  }

  return (
    <div className="min-h-screen">
      <SeoMeta
        title="Edit Chapter - Code Arena Admin"
        description="Edit chapter details"
        url={`https://www.code-arena.in/chapters/edit/${chapterId}`}
        image="/logo192.png"
      />

      <div className="ca-container max-w-3xl py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/chapter/${chapterId}`)}
          className="flex items-center gap-2 font-medium mb-6 transition-colors"
          style={{ color: 'var(--color-primary)' }}
        >
          <FaArrowLeft /> Back to Topics
        </button>

        {/* Form Card */}
        <div className="ca-card overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} px-4 sm:px-8 py-5 sm:py-6`}>
            <h1 className="text-3xl font-bold text-white">Edit Chapter</h1>
            <p className="text-white/80 mt-2">Update chapter details</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
            {/* Chapter Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Chapter Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`ca-input py-3 ${
                  errors.title
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Chapter Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Chapter Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="5"
                className={`ca-input py-3 ${
                  errors.description
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.description && (
                <p className="text-red-600 text-sm mt-1">{errors.description}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/chapter/${chapterId}`)}
                className="ca-btn-secondary flex-1 px-6 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ca-btn-primary flex-1 px-6 py-3"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
